/**
 * ToastOverlay Component
 *
 * Bridge between the notificationStore and Fluent UI's Toaster.
 * Uses Fluent UI Toaster for positioning, stacking, animations, and lifecycle,
 * but renders custom-styled toast content with left accent bar, icons,
 * and a dismiss button on every toast.
 *
 * Auto-dismiss: success/info 4s, warning 6s, error never auto-dismisses (requires manual dismiss).
 * Position: top-end (top-right).
 */

import { useEffect, useRef, useCallback, useId } from 'react';
import {
  Toaster,
  useToastController,
  Toast,
  ToastTrigger,
  Button,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  InfoRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import {
  useNotificationStore,
  type Notification,
  type NotificationType,
} from '@/stores/notificationStore';
import { getNotificationDisplayContent } from '@/utils/primitives';

const TIMEOUT_MS: Record<NotificationType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: -1, // Errors require manual dismiss — never auto-dismiss
};

const useStyles = makeStyles({
  // Reset default Toast styles (the "double background" fix)
  toastContainer: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    padding: 0,
    width: 'auto', // let content define width
    maxWidth: '100%',
    // Suppress scrollbar flashes during the slide-out exit animation.
    // Fluent's exit shrinks the toast width, briefly making inner content
    // overflow horizontally — clip it at every layer to avoid the flash.
    overflow: 'hidden',
    // Speed up the enter/exit animations (Fluent default is ~400ms)
    animationDuration: '200ms',
    '& [data-fui-toast]': {
      animationDuration: '200ms',
      overflow: 'hidden',
    },
    ':hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  },

  // Main Toast Container
  toast: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: '12px',
    alignItems: 'start',
    padding: '12px 16px',
    borderRadius: '12px', // More modern rounded corners
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)', // Custom refined shadow
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    width: '100%',
    maxWidth: '356px', // Standard specific width like Sonner
    minWidth: '300px',
    pointerEvents: 'auto',
    overflow: 'hidden', // Suppress scrollbar flashes during enter/exit animation
  },
  
  // Icon Styling
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    marginTop: '2px', // Align with title text
    fontSize: '20px',
  },
  
  // Icon Variants
  iconSuccess: { color: tokens.colorPaletteGreenForeground1 },
  iconError: { color: tokens.colorPaletteRedForeground1 },
  iconWarning: { color: tokens.colorPaletteYellowForeground1 },
  iconInfo: { color: tokens.colorBrandForeground1 },

  // Content Area
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: '2px', // Align with icon
    minWidth: 0, // Allow shrinking inside grid track so long messages wrap
  },
  title: {
    fontWeight: 600, // Semibold
    fontSize: '14px',
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'anywhere',
  },
  message: {
    fontSize: '13px',
    lineHeight: '18px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap', // Preserve line breaks
    overflowWrap: 'anywhere', // Break inside long unbroken strings (e.g. JSON error bodies)
    wordBreak: 'break-word', // Fallback for browsers that don't fully support overflowWrap: anywhere
    // Cap the message area so the toast stays compact; full message is always
    // accessible in the notification panel (bell icon).
    maxHeight: '120px',
    overflow: 'hidden',
  },

  // Close Button (Ghost style, always visible but subtle)
  dismiss: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },

  // Container for optional action button + dismiss button
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  // Action link rendered below the message inside the content column
  actionButton: {
    color: tokens.colorBrandForeground1,
    minWidth: 'auto',
    padding: 0,
    height: 'auto',
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
    alignSelf: 'flex-start',
  },
});

const ICON_MAP: Record<NotificationType, React.ReactNode> = {
  success: <CheckmarkCircleRegular />,
  error: <ErrorCircleRegular />,
  warning: <WarningRegular />,
  info: <InfoRegular />,
};

const ICON_STYLE_KEY = {
  success: 'iconSuccess',
  error: 'iconError',
  warning: 'iconWarning',
  info: 'iconInfo',
} as const;

/** Bridge that watches notificationStore and dispatches styled Fluent UI toasts. */
const ToastBridge: React.FC<{ toasterId: string }> = ({ toasterId }) => {
  const styles = useStyles();
  const { dispatchToast } = useToastController(toasterId);
  const notifications = useNotificationStore((state) => state.notifications);
  const markAsToasted = useNotificationStore((state) => state.markAsToasted);
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Dispatch a styled toast into the Fluent UI Toaster
  const dispatch = useCallback(
    (
      id: string,
      notification: Pick<Notification, 'type' | 'title' | 'message' | 'validationErrors'>,
      actionButton?: Notification['actionButton'],
      durationMs?: number,
    ) => {
      const { title, message } = getNotificationDisplayContent(notification);
      const { type } = notification;

      dispatchToast(
        <Toast className={styles.toastContainer}>
          <div className={styles.toast}>
            <span className={mergeClasses(styles.icon, styles[ICON_STYLE_KEY[type]])}>
              {ICON_MAP[type]}
            </span>
            
            <div className={styles.content}>
              {title && <span className={styles.title}>{title}</span>}
              {message && <span className={styles.message}>{message}</span>}
              {actionButton && (
                <ToastTrigger>
                  <Button
                    size="small"
                    appearance="transparent"
                    className={styles.actionButton}
                    onClick={() => {
                      removeNotification(id);
                      actionButton.onClick();
                    }}
                  >
                    {actionButton.label}
                  </Button>
                </ToastTrigger>
              )}
            </div>

            <div className={styles.actions}>
              <ToastTrigger>
                <button
                  type="button"
                  className={styles.dismiss}
                  aria-label="Dismiss"
                >
                  <DismissRegular fontSize={16} />
                </button>
              </ToastTrigger>
            </div>
          </div>
        </Toast>,
        {
          intent: type,
          timeout: durationMs ?? TIMEOUT_MS[type],
          toastId: id,
        },
      );
    },
    [dispatchToast, styles, removeNotification],
  );

  useEffect(() => {
    for (const n of notifications) {
      // seenIdsRef prevents duplicate dispatches within a render batch (synchronous guard),
      // while n.toasted persists the flag to sessionStorage so rehydrated notifications aren't re-shown.
      if (!n.toasted && !seenIdsRef.current.has(n.id)) {
        seenIdsRef.current.add(n.id);
        dispatch(n.id, n, n.actionButton, n.durationMs);
        markAsToasted(n.id);
      }
    }
  }, [notifications, dispatch, markAsToasted]);

  return null;
};

export const ToastOverlay: React.FC = () => {
  const toasterId = useId();

  return (
    <>
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        limit={5}
      />
      <ToastBridge toasterId={toasterId} />
    </>
  );
};
